import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import { SkipLink } from './components/Accessibility';
import Home from './pages/Home';
import NewHomePage from './pages/NewHomePage';
import Collections from './pages/Collections';
// ProductDetail will be lazy loaded
import Cart from './pages/Cart';
import HowToMeasure from './pages/HowToMeasure';
import About from './pages/About';
import BespokeConsultation from './pages/BespokeConsultation';
import Contact from './pages/Contact';
import SavedMeasurements from './pages/SavedMeasurements';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import PhoneLogin from './pages/PhoneLogin';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import OrderHistory from './pages/OrderHistory';
import OrderDetail from './pages/OrderDetail';
import OrderTracking from './pages/OrderTracking';
import SizeIssueReportPage from './pages/SizeIssueReportPage';
import Addresses from './pages/Addresses';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CancellationRefunds from './pages/CancellationRefunds';
import Shipping from './pages/Shipping';
import Loader from './components/Loader';
import ScrollToTop from './components/ScrollToTop';

import { CartProvider } from './context/CartContext'; // Import CartProvider
import { AuthProvider, useAuth } from './context/AuthContext'; // Import AuthProvider and useAuth
import { CategoriesProvider } from './context/CategoriesContext'; // Import CategoriesProvider
import { DiscountProvider } from './context/DiscountContext'; // Import DiscountProvider
import { OrderProvider } from './context/OrderContext'; // Import OrderProvider
import { StyleCreditsProvider } from './context/StyleCreditsContext'; // Import StyleCreditsProvider
import { SavedMeasurementsProvider } from './context/SavedMeasurementsContext'; // Import SavedMeasurementsProvider
import { useLoader } from './hooks/useLoader';
import './App.css';

// Lazy loaded components - loaded only when needed using dynamic imports
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const AdminRoute = lazy(() => import('./components/AdminRoute'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));

// Protected Route component for redirecting logged-in users
const ProtectedLoginRoute = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  
  if (currentUser) {
    // Check if there's a returnUrl parameter
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    
    if (returnUrl) {
      return <Navigate to={returnUrl} replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }
  
  return children;
};

// Main content wrapper with conditional class
const MainContent = ({ children }) => {
  const location = useLocation();
  const isProductDetail = location.pathname.startsWith('/product/');
  
  return (
    <main 
      id="main-content" 
      role="main" 
      className={isProductDetail ? 'product-detail-main' : ''}
    >
      {children}
    </main>
  );
};

function App() {
  const { isLoading } = useLoader();

  return (
    <HelmetProvider>
      <AuthProvider> {/* Wrap app with AuthProvider for authentication */}
        <StyleCreditsProvider> {/* Wrap app with StyleCreditsProvider for credits management */}
          <CartProvider> {/* Wrap app with CartProvider for cart state management */}
            <CategoriesProvider> {/* Wrap app with CategoriesProvider for shared categories */}
              <DiscountProvider> {/* Wrap app with DiscountProvider for discount management */}
                <OrderProvider> {/* Wrap app with OrderProvider for order management */}
                  <SavedMeasurementsProvider> {/* Wrap app with SavedMeasurementsProvider for saved measurements */}
            <Router>
              <ScrollToTop />
              <div className="App">
                {/* Accessibility: Skip to main content */}
                <SkipLink />
                
                {/* Full Page Loader */}
                <Loader isLoading={isLoading} />
                
                <Header />
                <MainContent>
                <Routes>
                  <Route path="/" element={<NewHomePage />} />
                  <Route path="/home" element={<Navigate to="/" replace />} />
                  <Route path="/old-home" element={<Home />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/product/:id" element={
                    <Suspense fallback={<Loader isLoading={true} />}>
                      <ProductDetail />
                    </Suspense>
                  } />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/how-to-measure" element={<HowToMeasure />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/bespoke-consultation" element={<BespokeConsultation />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/saved-measurements" element={<SavedMeasurements />} />
                  <Route path="/login" element={
                    <ProtectedLoginRoute>
                      <Login />
                    </ProtectedLoginRoute>
                  } />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/order-history" element={<OrderHistory />} />
                  <Route path="/addresses" element={<Addresses />} />
                  <Route path="/order-detail/:orderId" element={<OrderDetail />} />
                  <Route path="/track-order" element={<OrderTracking />} />
                  <Route path="/size-issue-report" element={<SizeIssueReportPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/cancellation-refunds" element={<CancellationRefunds />} />
                  <Route path="/shipping" element={<Shipping />} />
                  <Route path="/admin" element={
                    <Suspense fallback={<Loader isLoading={true} />}>
                      <AdminRoute>
                        <AdminPortal />
                      </AdminRoute>
                    </Suspense>
                  } />
                  <Route path="/admin-login" element={
                    <Suspense fallback={<Loader isLoading={true} />}>
                      <AdminLogin />
                    </Suspense>
                  } />
                  {/* Catch-all route for any invalid/weird routes - redirect to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </MainContent>
              <Footer />
            </div>
            </Router>
                  </SavedMeasurementsProvider>
                </OrderProvider>
              </DiscountProvider>
            </CategoriesProvider>
          </CartProvider>
        </StyleCreditsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App; 